/**
 * Global fetch interceptor to handle HTTP 401 Unauthorized responses.
 *
 * Behavior:
 * - Intercepts all fetch calls to API_BASE_URL.
 * - If response.status === 401, dispatches a single window event 'auth:expired'.
 * - Throttles events to avoid spamming when multiple requests fail at once.
 * - Skips auth endpoints (login/validate/logout/refresh/google) to avoid false positives.
 */

import { API_BASE_URL } from "@/lib/apiConfig";

// Use a symbol/flag on the global scope to ensure we patch only once
const g = globalThis as any;
if (!g.__FETCH_401_INTERCEPTOR_INSTALLED__) {
  g.__FETCH_401_INTERCEPTOR_INSTALLED__ = true;

  const originalFetch: typeof fetch = g.fetch.bind(g);

  let lastDispatch = 0;
  const THROTTLE_MS = 3000;

  const isApiUrl = (url: string) => {
    try {
      // Normalize URL for comparison
      const normalizedApi = API_BASE_URL.replace(/\/$/, "");
      const u = new URL(url, window.location.origin);
      if (u.href.startsWith(normalizedApi)) return true;
      // Also consider same-origin relative calls like "/api/..."
      return u.pathname.startsWith("/api");
    } catch {
      return false;
    }
  };

  const isExcludedAuthPath = (url: string) => {
    try {
      const u = new URL(url, window.location.origin);
      const path = u.pathname.replace(/\/$/, "");
      // Exclude auth endpoints to avoid triggering logout during login/validate/logout/refresh
      return (
        path.endsWith("/auth/login") ||
        path.endsWith("/auth/validate") ||
        path.endsWith("/auth/logout") ||
        path.endsWith("/auth/refresh") ||
        path.endsWith("/auth/google")
      );
    } catch {
      return false;
    }
  };

  // Patch global fetch
  g.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === "string" || input instanceof URL ? String(input) : (input as Request).url;

    const response = await originalFetch(input as any, init);

    try {
      if (response && response.status === 401 && isApiUrl(url) && !isExcludedAuthPath(url)) {
        const now = Date.now();
        if (now - lastDispatch > THROTTLE_MS) {
          lastDispatch = now;
          // Fire a CustomEvent with response context (do not consume the body)
          const event = new CustomEvent("auth:expired", {
            detail: {
              status: response.status,
              url,
            },
          });
          window.dispatchEvent(event);
        }
      }
    } catch {
      // Swallow any interceptor errors to not affect app behavior
    }

    return response;
  };
}
